import { useCallback } from 'react';

import { upsertMessageInList } from '../../pages/PlanningPageHelpers';
import { errorMessage } from '../../services/apiError';
import { chatService, ChatMessage, DEFAULT_CHAT_MODEL } from '../../services/chatService';
import type { SupervisorButton } from '../../types/supervisorButtons';
import { trackChatMessage } from '../../utils/analytics';

import { dispatchSuggestedAgentsEvent } from './suggestionUtils';
import { ChatState, StreamingState, StreamingRefsFull, Ticker } from './types';
import { bindStreamingMessageId, handleStreamError, resetStreamingState } from './useChatBoxHelpers';

interface SenderProps {
  projectId: string | null;
  activeSessionId?: string | null;
  refsFull: StreamingRefsFull;
  ticker: Ticker;
  chatState: ChatState;
  streamingState: StreamingState;
  getOrderedTimestamp: () => string;
}

interface StreamCallbacks {
  onUserMessageId: (id: string) => void;
  onAssistantMessageId?: (id: string) => void;
  onChunk: (c: string) => void;
  onComplete: (params: StreamCallbackParams) => void;
  onError: (e: string) => void;
  onWarning?: (msg: string) => void;
  onStreamEnd: () => void;
  onSuggestedAgents?: (
    agents: string[],
    buttons: SupervisorButton[],
    projectId: string,
    messageId: string,
    sessionId?: string | null,
    promptScope?: Record<string, unknown> | null,
    aiContent?: string | null
  ) => void;
}

interface StreamCallbackParams {
  aiMessageId: string;
  userMessageId?: string;
  sessionId?: string | null;
}

const resolveSessionId = (
  params: StreamCallbackParams,
  fallbackSessionId?: string | null,
) => params.sessionId || fallbackSessionId || null;

const createOnChunk = (refsFull: StreamingRefsFull, ticker: Ticker) => (c: string) => {
  refsFull.appendToBuffer(c);
  ticker.enqueueTokens(c);
};

const preserveActionMetadata = (
  message: ChatMessage,
  existing?: ChatMessage,
): ChatMessage => ({
  ...message,
  phase: message.phase ?? existing?.phase ?? null,
  allowed_buttons: message.allowed_buttons ?? existing?.allowed_buttons ?? null,
  buttons: message.buttons ?? existing?.buttons ?? null,
  clicked_button: message.clicked_button ?? existing?.clicked_button ?? null,
  clicked_buttons: message.clicked_buttons ?? existing?.clicked_buttons ?? null,
});

const createFinalizeCallback = (
  projectId: string,
  chatState: ChatState,
  getOrderedTimestamp: () => string,
  refsFull: StreamingRefsFull,
  streamingState: StreamingState,
  tempMsg: ChatMessage,
  tempId: string,
  now: string,
  activeSessionId?: string | null,
) => (params: StreamCallbackParams, buffer: string) => {
  const ts = getOrderedTimestamp();
  const sessionId = resolveSessionId(params, activeSessionId);
  const userMsg = {
    ...tempMsg,
    id: params.userMessageId || tempId,
    project_id: projectId,
    session_id: sessionId,
    created_at: now,
    updated_at: ts
  };

  const aiMsg: ChatMessage | undefined = buffer.trim()
    ? {
      id: params.aiMessageId,
      user_id: 'current-user',
      content: buffer,
      author: 'assistant',
      project_id: projectId,
      session_id: sessionId,
      created_at: ts,
      updated_at: ts
    }
    : undefined;

  chatState.setMessages((prev) => {
    let r = prev.filter((m) => m.id !== tempId);

    r = upsertMessageInList(r, userMsg);
    if (aiMsg) {
      const existingAiMsg = r.find((m) => m.id === aiMsg.id);
      r = upsertMessageInList(r, preserveActionMetadata(aiMsg, existingAiMsg));
    }

    return r;
  });

  resetStreamingState(refsFull, streamingState, chatState);
  chatState.setIsLoading(false);
  refsFull.setIsUserStreaming(false);
};

const createOnComplete = (
  refsFull: StreamingRefsFull,
  streamingState: StreamingState,
  capturedUserMsgId: { current: string },
  finalize: (params: StreamCallbackParams, buffer: string) => void
) => (params: StreamCallbackParams) => {
  bindStreamingMessageId(refsFull, streamingState, params.aiMessageId);
  const buffer = refsFull.streamingBufferRef.current;
  const finalizer = () => {
    finalize(
      { ...params, userMessageId: capturedUserMsgId.current || params.userMessageId },
      buffer
    );
  };
  const isEmpty = refsFull.tokenQueueRef.current.length === 0 && !refsFull.tokenIntervalRef.current;

  if (isEmpty) finalizer();
  else refsFull.setPendingCompletion({ finalizeCallback: finalizer });
};

function resetSenderRefs(refsFull: StreamingRefsFull, ticker: Ticker) {
  refsFull.setIsUserStreaming(true);
  refsFull.abortStream();
  refsFull.setStreamingBuffer('');
  refsFull.shouldAutoScrollRef.current = true;
  ticker.resetStreamingDisplay();
  refsFull.focusInput();
}

function resetSenderChatState(chatState: ChatState) {
  chatState.setIsLoading(true);
  chatState.setError(null);
  chatState.setInputValue('');
}

function resetSenderUI(refsFull: StreamingRefsFull, ticker: Ticker, chatState: ChatState) {
  resetSenderRefs(refsFull, ticker);
  resetSenderChatState(chatState);
}

const setupSending = (
  refsFull: StreamingRefsFull,
  ticker: Ticker,
  chatState: ChatState,
  streamingState: StreamingState,
  content: string,
  projectId: string,
  activeSessionId?: string | null,
  mediaUrls?: string[]
) => {
  resetSenderUI(refsFull, ticker, chatState);

  const tempId = `temp-${Date.now()}`;
  const now = new Date().toISOString();
  // project_id is required: visibleMessages filters on it, so without it the
  // optimistic bubble stays invisible for the whole stream duration.
  const tempMsg: ChatMessage = {
    id: tempId, user_id: 'current-user', content, author: 'user', created_at: now, updated_at: now,
    project_id: projectId,
    session_id: activeSessionId || null,
    media_urls: mediaUrls || null
  };

  chatState.setMessages((prev) => [...prev, tempMsg]);
  streamingState.setIsAiTyping(true);

  return { tempId, now, tempMsg };
};

const createOnError = (
  chatState: ChatState,
  streamingState: StreamingState,
  refsFull: StreamingRefsFull,
  ticker: Ticker,
  tempId: string
) => (e: string) => {
  handleStreamError(e, chatState, streamingState, refsFull, ticker.resetStreamingDisplay, tempId);
  chatState.setMessages((prev) => prev.filter((m) => m.id !== tempId));
};

async function executeStream(
  content: string,
  projectId: string,
  refsFull: StreamingRefsFull,
  callbacks: StreamCallbacks,
  activeSessionId?: string | null,
  mediaUrls?: string[]
) {
  const stream = await chatService.sendMessageWithAIStream(
    content,
    DEFAULT_CHAT_MODEL,
    projectId,
    callbacks,
    mediaUrls,
    false,
    activeSessionId,
    undefined,
    undefined,
    undefined,
    // Assign the controller when the request STARTS: handleEmptyQueue treats a
    // null controller as "stream finished" and would otherwise tear down live
    // streaming state on any >50ms token lull.
    (ctrl) => refsFull.setStreamController(ctrl),
  );

  refsFull.setStreamController(stream);
}

async function internalSend(
  props: SenderProps,
  content: string,
  tempMsg: ChatMessage,
  tempId: string,
  now: string,
  mediaUrls?: string[]
) {
  const { projectId, activeSessionId, refsFull, ticker, chatState, streamingState, getOrderedTimestamp } = props;

  if (!projectId) return;

  const capturedUserMsgId = { current: '' };
  const finalize = createFinalizeCallback(
    projectId, chatState, getOrderedTimestamp, refsFull, streamingState, tempMsg, tempId, now, activeSessionId
  );
  
  const onError = createOnError(chatState, streamingState, refsFull, ticker, tempId);

  //
  //
  const opts: StreamCallbacks = {
    onUserMessageId: (id: string) => { capturedUserMsgId.current = id; },
    onAssistantMessageId: (id: string) => bindStreamingMessageId(refsFull, streamingState, id),
    onChunk: createOnChunk(refsFull, ticker),
    onComplete: createOnComplete(refsFull, streamingState, capturedUserMsgId, finalize),
    onError,
    onWarning: (msg: string) => { chatState.setError(msg); },
    onSuggestedAgents: (
      agents: string[],
      buttons: SupervisorButton[],
      pid: string,
      mid: string,
      sessionId?: string | null,
      promptScope?: Record<string, unknown> | null,
      aiContent?: string | null,
    ) => {
      dispatchSuggestedAgentsEvent({
        agents, buttons, projectId: pid, messageId: mid,
        sessionId: sessionId ?? activeSessionId ?? null,
        promptScope: promptScope ?? null,
        aiContent: aiContent ?? refsFull.streamingBufferRef.current,
      });
    },
    onStreamEnd: () => {
      refsFull.setStreamController(null);
      window.dispatchEvent(new Event('chat_sessions_changed'));
      window.dispatchEvent(new Event('chat_stream_end'));
      if (!refsFull.pendingCompletionRef.current && refsFull.tokenQueueRef.current.length === 0) {
        resetStreamingState(refsFull, streamingState, chatState);
      }
    }
  };

  await executeStream(content, projectId, refsFull, opts, activeSessionId, mediaUrls);
}

const handleSendError = (
  refsFull: StreamingRefsFull,
  chatState: ChatState,
  streamingState: StreamingState,
  ticker: Ticker,
  tempId: string,
  error: unknown,
) => {
  handleStreamError(
    errorMessage(error, 'We couldn’t send your message. Check your connection and try again.'),
    chatState,
    streamingState,
    refsFull,
    ticker.resetStreamingDisplay,
    tempId,
  );
  chatState.setMessages((prev) => prev.filter((m) => m.id !== tempId));
};

async function sendMessageOrchestrator(
  props: SenderProps,
  content: string,
  mediaUrls?: string[]
) {
  const { projectId, refsFull, ticker, chatState, streamingState } = props;

  if (!projectId) return;

  const { tempId, now, tempMsg } = setupSending(
    refsFull,
    ticker,
    chatState,
    streamingState,
    content,
    projectId,
    props.activeSessionId,
    mediaUrls,
  );

  try {
    await internalSend(props, content, tempMsg, tempId, now, mediaUrls);
    trackChatMessage();
  } catch (error) {
    handleSendError(refsFull, chatState, streamingState, ticker, tempId, error);
  }
}

export const useMessageSender = (props: SenderProps) => useCallback(
  async (content: string, mediaUrls?: string[]) => {
    await sendMessageOrchestrator(props, content, mediaUrls);
  }, [props]
);
